import React, { useMemo } from "react";
import { StatusBadge } from "../../../../constants/statusConstants";
import CustomTable from "../../components/CustomTable";
import Loading from "../../components/Loading";
import MobilePatternHeader from "../../components/MobilePatternHeader";
import TableSkeleton from "../../components/TableSkeleton";
import Button from "../../components/Button";
import { ASSET_PLACEHOLDER_IMAGE } from "../utils/assetFormatters";
import AssetFilterBar from "./AssetFilterBar";
import AssetMobileList from "./AssetMobileList";
import AssetReportSummary from "./AssetReportSummary";

const AssetManagementPresenter = ({
  assets,
  currentPage,
  error,
  exporting,
  fromDate,
  hasLoadedOnce,
  isMobile,
  itemsPerPage,
  loading,
  report,
  search,
  toDate,
  totalPages,
  totalProfiles,
  onBack,
  onItemsPerPageChange,
  onPageChange,
  onRetry,
  onExportPdf,
  onSearch,
  onSelectAsset,
  setFromDate,
  setSearch,
  setToDate,
}) => {
  const columns = useMemo(
    () => [
      { key: "id", title: "STT", minWidth: "56px" },
      {
        key: "image",
        title: "Mẫu",
        minWidth: "110px",
        align: "text-center",
        render: (value) => (
          <div className="flex items-center justify-center">
            <img
              src={value || ASSET_PLACEHOLDER_IMAGE}
              alt="Tài sản"
              className="size-16 rounded-md object-contain ring-1 ring-slate-100"
              onError={(event) => {
                event.currentTarget.src = ASSET_PLACEHOLDER_IMAGE;
              }}
            />
          </div>
        ),
      },
      { key: "brand", title: "Tên gọi", minWidth: "180px" },
      { key: "classification", title: "Phân loại", minWidth: "120px" },
      { key: "profileCode", title: "Số đơn", minWidth: "120px" },
      { key: "submittedDate", title: "Ngày nộp đơn", minWidth: "140px" },
      { key: "certificateNumber", title: "Số bằng", minWidth: "120px" },
      { key: "owner", title: "Chủ sở hữu", minWidth: "160px" },
      {
        key: "status",
        title: "Tình trạng",
        minWidth: "140px",
        render: (status) => <StatusBadge status={status} />,
      },
    ],
    [],
  );

  if (isMobile) {
    return (
      <div className="h-full w-full bg-[#F2F2F2]">
        <MobilePatternHeader
          title="Danh mục tài sản trí tuệ"
          onBack={onBack}
        />

        <div className="h-[calc(100%-64px)] overflow-auto p-3 pb-24">
          <div className="mb-3">
            <AssetReportSummary
              chart={report.chart}
              error={report.error}
              loading={report.loading}
              onRetry={report.onRetry}
              rows={report.rows}
              summary={report.summary}
              variant="chart"
            />
          </div>

          <div className="mb-3">
            <AssetFilterBar
              fromDate={fromDate}
              exporting={exporting}
              search={search}
              setFromDate={setFromDate}
              setSearch={setSearch}
              setToDate={setToDate}
              toDate={toDate}
              onExportPdf={onExportPdf}
              onSearch={onSearch}
              loading={loading}
              variant="mobile"
            />
          </div>

          <MobileOriginalProfileList
            assets={assets}
            currentPage={currentPage}
            error={error}
            hasLoadedOnce={hasLoadedOnce}
            loading={loading}
            onPageChange={onPageChange}
            onRetry={onRetry}
            onSelectAsset={onSelectAsset}
            totalPages={totalPages}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col space-y-6">
      <h1 className="text-2xl font-sf-semibold text-gray-800">
        Danh mục tài sản trí tuệ
      </h1>

      <section className="w-full flex min-h-0 flex-1 flex-col gap-4 rounded-[16px] border border-[#D2D5DA] bg-white p-4 shadow-sm">
        <AssetFilterBar
          fromDate={fromDate}
          exporting={exporting}
          search={search}
          setFromDate={setFromDate}
          setSearch={setSearch}
          setToDate={setToDate}
          toDate={toDate}
          onExportPdf={onExportPdf}
          onSearch={onSearch}
          loading={loading}
        />

        <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 xl:grid-cols-[minmax(320px,0.45fr)_minmax(0,1fr)]">
          <AssetReportSummary
            chart={report.chart}
            error={report.error}
            loading={report.loading}
            onRetry={report.onRetry}
            rows={report.rows}
            summary={report.summary}
            variant="chart"
          />

          <OriginalProfileList
            assets={assets}
            columns={columns}
            currentPage={currentPage}
            error={error}
            hasLoadedOnce={hasLoadedOnce}
            itemsPerPage={itemsPerPage}
            loading={loading}
            onItemsPerPageChange={onItemsPerPageChange}
            onPageChange={onPageChange}
            onRetry={onRetry}
            onSelectAsset={onSelectAsset}
            totalPages={totalPages}
          />
        </div>
      </section>
    </div>
  );
};

const OriginalProfileList = ({
  assets,
  columns,
  currentPage,
  error,
  hasLoadedOnce,
  itemsPerPage,
  loading,
  onItemsPerPageChange,
  onPageChange,
  onRetry,
  onSelectAsset,
  totalPages,
}) => {
  if (loading && !hasLoadedOnce) {
    return (
      <div className="min-h-[360px]">
        <TableSkeleton rows={4} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[360px] flex-col items-center justify-center gap-3 rounded-[12px] border border-[#E3E3E3] bg-white text-center">
        <p className="text-sm text-red-600">{error}</p>
        <Button
          type="button"
          variant="primary"
          size="lg"
          onClick={onRetry}
        >
          Thử lại
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-0 min-w-0 overflow-hidden">
      <CustomTable
        data={assets}
        columns={columns}
        currentPage={currentPage}
        setCurrentPage={onPageChange}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={onItemsPerPageChange}
        setView={onSelectAsset}
        loading={loading && hasLoadedOnce}
        onRowClick={onSelectAsset}
      />
    </div>
  );
};

const MobileOriginalProfileList = ({
  assets,
  currentPage,
  error,
  hasLoadedOnce,
  loading,
  onPageChange,
  onRetry,
  onSelectAsset,
  totalPages,
}) => {
  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  return (
    <>
      {loading && !hasLoadedOnce ? (
        <div className="flex h-[50vh] items-center justify-center">
          <Loading />
        </div>
      ) : error ? (
        <div className="flex h-[50vh] flex-col items-center justify-center gap-3 text-center">
          <p className="text-sm text-red-600">{error}</p>
          <Button
            type="button"
            variant="primary"
            size="lg"
            onClick={onRetry}
          >
            Thử lại
          </Button>
        </div>
      ) : (
        <>
          <AssetMobileList
            assets={assets}
            hasLoadedOnce={hasLoadedOnce}
            onSelectAsset={onSelectAsset}
          />
          {assets.length > 0 && totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 py-4 text-sm">
              <button
                type="button"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={!canGoPrevious || loading}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Trước
              </button>
              <span className="text-gray-600">
                Trang {currentPage}/{totalPages}
              </span>
              <button
                type="button"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={!canGoNext || loading}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Sau
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default AssetManagementPresenter;
